import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { confirmDialog } from '../confirm-dialog.js';
import { describeSeat, seatBadgeClass } from '../linkedin-seat.js';
import { openAuthPopup } from '../oauth-popup.js';
import { emailConnectFlowEnded, emailConnectSucceeded } from './email-connect-outcome.js';
import { skeletonLine } from '../skeleton.js';
import { toast } from '../toast.js';
import type { SalesRep } from '../wizard/types.js';
// CRM provider info + connection list now travel over baseQL instead of REST.
import type { CrmConnectionView } from '../db/baseql/crm-connection-view.js';
import {
  disconnectCrmConnection,
  getCrmConfig,
  getHubspotLeadStatusMapping,
  listCrmConnections,
  saveHubspotLeadStatusMapping,
  suggestHubspotLeadStatusMapping,
} from '../db/baseql/crm-ops.js';
import type { HubspotLeadStatusMappingView } from '../db/baseql/hubspot-lead-status-mapping-view.js';

interface ConnectedEmail {
  email: string;
  name: string;
  aiEmployeeId: string | null;
  totalSent?: number;
  /**
   * Which integration this mailbox belongs to.
   *
   * Both kinds appear in one list, and they are NOT interchangeable: assigning
   * or disconnecting hits a different provider for each, so a row that does not
   * carry its own provider gets acted on as an Instantly one. Absent means
   * Instantly, which is what every row was before Unipile email existed.
   */
  provider?: 'instantly_email' | 'unipile_email';
  /**
   * Whether an operator has marked this mailbox already-warmed, and the daily
   * send cap that follows (the 15/35/75 ramp day, or the full 100 when warmed).
   * Unipile mailboxes only — Instantly does its own warm-up and carries neither.
   */
  emailWarmedManually?: boolean;
  dailyLimit?: number;
}
interface ConnectedLinkedin {
  id: string;
  name: string;
  status?: string;
  aiEmployeeId: string | null;
  totalInvitesSent?: number;
  totalDmsSent?: number;
  /**
   * `connection_params.im.premiumFeatures`, straight from the provider.
   *
   * Absent means LinkedIn did not say — which is NOT the same as an empty list,
   * and `describeSeat` keeps them apart. See linkedin-seat.ts.
   */
  premiumFeatures?: string[] | null;
}

type CrmConnection = CrmConnectionView;

/** One soft-deleted campaign, as served by GET /api/campaigns/deleted. */
interface DeletedCampaign {
  _id: string;
  name: string;
  campaignType: string;
  /** The status it will be restored to (already normalised server-side). */
  statusBeforeDelete?: string;
  deletedAt: string;
  /** Computed server-side — a countdown stored in the document would be stale the next day. */
  daysLeft?: number;
  purgeAt?: string;
  leadsProcessed?: number;
  totalSent?: number;
}

/**
 * A target Sales Rep for a freshly connected account: either a concrete id, or a
 * resolver that lazily creates/returns one. The resolver runs only AFTER OAuth
 * succeeds (just before assignment), so the popup opens with the same latency as
 * before and no rep is created when the user abandons the OAuth flow.
 */
type RepIdTarget = string | (() => Promise<string | null>);

async function resolveRepId(target: RepIdTarget | undefined): Promise<string | null> {
  if (target == null) return null;
  return typeof target === 'function' ? await target() : target;
}

export const Settings = stateful(() => {
  const [emailAccounts, setEmailAccounts] = useState<ConnectedEmail[]>([]);
  const [linkedinAccounts, setLinkedinAccounts] = useState<ConnectedLinkedin[]>([]);
  const [error, setError] = useState('');
  // Local 'success' state retained so existing setSuccess('') clears between flows; toasts are the user-visible feedback.
  const [, setSuccess] = useState('');
  const notifySuccess = (m: string) => {
    setSuccess(m);
    toast.success(m);
  };
  const notifyError = (m: string) => {
    setError(m);
    toast.error(m);
  };
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [editingEmployee, setEditingEmployee] = useState<Partial<SalesRep> | null>(null);
  const [savingEmployee, setSavingEmployee] = useState(false);
  // Id of a Sales Rep that was implicitly created so the connect flow could
  // attach an account to it (OAuth assignment needs the rep id). Tracked so we
  // can clean it up if the user cancels before connecting any account.
  const [autoCreatedRepId, setAutoCreatedRepId] = useState<string | null>(null);
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const [connectingEmail, setConnectingEmail] = useState(false);
  const [connectingLinkedin, setConnectingLinkedin] = useState(false);

  // CRM integrations
  const [crmConfig, setCrmConfig] = useState<{
    hubspot: { enabled: boolean; mode?: string; oauthAvailable?: boolean };
  }>({ hubspot: { enabled: false } });
  const [crmConnections, setCrmConnections] = useState<CrmConnection[]>([]);
  const [connectingCrm, setConnectingCrm] = useState(false);

  // HubSpot Lead Status mapping panel (Teil 2): open connection + its loaded view
  // + the operator's draft (localStatus -> chosen option value, '' = leave unset).
  const [mappingConnId, setMappingConnId] = useState<string | null>(null);
  const [mappingView, setMappingView] = useState<HubspotLeadStatusMappingView | null>(null);
  const [mappingDraft, setMappingDraft] = useState<Record<string, string>>({});
  const [mappingBusy, setMappingBusy] = useState<'' | 'load' | 'suggest' | 'save'>('');
  const [mappingError, setMappingError] = useState<string | null>(null);

  // Locked / "coming soon" CRM providers — UI only, gated behind sales request.
  const lockedCrmProviders: { id: string; name: string; icon: string }[] = [
    { id: 'salesforce', name: 'Salesforce', icon: 'icon-[material-symbols--cloud]' },
    { id: 'zoho', name: 'Zoho CRM', icon: 'icon-[material-symbols--workspaces]' },
    { id: 'pipedrive', name: 'Pipedrive', icon: 'icon-[material-symbols--trending-up]' },
    { id: 'dynamics365', name: 'Dynamics 365', icon: 'icon-[material-symbols--grid-view]' },
    { id: 'monday', name: 'monday CRM', icon: 'icon-[material-symbols--calendar-month]' },
  ];

  const [loading, setLoading] = useState(false);

  // ── Deleted campaigns (the recycle bin) ───────────────────────────────
  const [deletedCampaigns, setDeletedCampaigns] = useState<DeletedCampaign[]>([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loadingDeleted, setLoadingDeleted] = useState(true);
  /** Campaign id with a restore/purge request in flight. */
  const [deletedBusy, setDeletedBusy] = useState('');

  // Load accounts after first render
  useEffect(() => {
    void loadAccounts();
    void loadDeletedCampaigns();
  }, []);

  async function loadDeletedCampaigns() {
    setLoadingDeleted(true);
    try {
      const resp = await fetch('/api/campaigns/deleted');
      const data = await resp.json();
      if (data.ok) {
        setDeletedCampaigns(data.campaigns ?? []);
        if (typeof data.retentionDays === 'number') setRetentionDays(data.retentionDays);
      }
    } catch {
      // Non-fatal: the rest of Settings is unaffected, and the section renders
      // as empty rather than blocking the page on a network blip.
    }
    setLoadingDeleted(false);
  }

  async function restoreCampaign(c: DeletedCampaign) {
    setDeletedBusy(c._id);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(c._id)}/restore`, { method: 'POST' });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        notifyError(data?.error ?? 'Failed to restore campaign');
        return;
      }
      setDeletedCampaigns(deletedCampaigns.filter(x => x._id !== c._id));
      // A restored campaign never resumes sending on its own — say which state
      // it came back in so nobody assumes outreach just restarted.
      notifySuccess(
        data.status === 'paused'
          ? `“${c.name || 'Untitled'}” restored — it is paused, resume it when you want it sending again.`
          : `“${c.name || 'Untitled'}” restored to your dashboard.`,
      );
    } catch {
      notifyError('Failed to restore campaign (network error)');
    } finally {
      setDeletedBusy('');
    }
  }

  async function purgeCampaign(c: DeletedCampaign) {
    // The one irreversible action in this flow, so it says exactly what goes
    // and names what cannot be undone.
    if (
      !(await confirmDialog(
        `Permanently delete “${c.name || 'Untitled'}”? This removes the campaign, its leads and every generated message for good. This cannot be undone.`,
        { title: 'Delete Permanently', confirmLabel: 'Delete permanently', confirmClass: 'btn-error' },
      ))
    )
      return;
    setDeletedBusy(c._id);
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(c._id)}/permanent`, { method: 'DELETE' });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || !data?.ok) {
        notifyError(data?.error ?? 'Failed to delete campaign');
        return;
      }
      setDeletedCampaigns(deletedCampaigns.filter(x => x._id !== c._id));
      notifySuccess('Campaign permanently deleted.');
      if (data.warning) toast.warning(data.warning);
    } catch {
      notifyError('Failed to delete campaign (network error)');
    } finally {
      setDeletedBusy('');
    }
  }

  async function loadAccounts() {
    setLoading(true);
    try {
      const [emailRes, unipileEmailRes, linkedinRes, aiRes, crmCfg, crmConns] = await Promise.all([
        fetch('/api/accounts/email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
        fetch('/api/accounts/unipile-email')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
        fetch('/api/accounts/linkedin')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
        fetch('/api/ai-employees')
          .then(r => r.json())
          .catch(() => ({ ok: false })),
        getCrmConfig()({ hubspot: { enabled: 1, mode: 1, oauthAvailable: 1 } }).catch(() => null),
        listCrmConnections()({
          _id: 1,
          provider: 1,
          accountId: 1,
          accountLabel: 1,
          authMode: 1,
          isSystem: 1,
          status: 1,
          scopes: 1,
          connectedAt: 1,
          lastUsedAt: 1,
          lastError: 1,
        }).catch(() => [] as CrmConnectionView[]),
      ]);
      // Both integrations feed one list, each row saying which it came from.
      // A Unipile mailbox is keyed by the provider's account id rather than by
      // an address: Instantly identifies a mailbox by its email, Unipile by an
      // opaque id, and the assign route reads whichever the row carries.
      const instantlyEmails: ConnectedEmail[] = emailRes.ok ? (emailRes.accounts ?? []) : [];
      const unipileEmails: ConnectedEmail[] = unipileEmailRes.ok
        ? (unipileEmailRes.accounts ?? []).map((a: any) => ({
            email: a.id,
            name: a.email ?? a.displayName ?? a.id,
            aiEmployeeId: a.aiEmployeeId ?? null,
            totalSent: a.totalSent ?? 0,
            provider: 'unipile_email' as const,
            emailWarmedManually: a.emailWarmedManually === true,
            dailyLimit: a.dailyLimit,
          }))
        : [];
      setEmailAccounts([...instantlyEmails, ...unipileEmails]);
      if (linkedinRes.ok) setLinkedinAccounts(linkedinRes.accounts ?? []);
      if (aiRes.ok) setSalesReps(aiRes.aiEmployees ?? []);
      setCrmConfig({
        hubspot: {
          enabled: !!crmCfg?.hubspot?.enabled,
          mode: crmCfg?.hubspot?.mode,
          oauthAvailable: !!crmCfg?.hubspot?.oauthAvailable,
        },
      });
      setCrmConnections(crmConns ?? []);
    } finally {
      setLoading(false);
    }
  }

  async function saveSalesRep() {
    if (!editingEmployee) return;
    setSavingEmployee(true);
    setError('');
    try {
      const isNew = !editingEmployee._id;
      const url = isNew ? '/api/ai-employees' : `/api/ai-employees/${encodeURIComponent(editingEmployee._id!)}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingEmployee),
      });
      const data = await res.json();
      if (data.ok) {
        notifySuccess(isNew ? 'Sales Representative created.' : 'Sales Representative updated.');
        setEditingEmployee(null);
        setAutoCreatedRepId(null);
        void loadAccounts();
      } else {
        notifyError(data.error ?? 'Failed to save Sales Representative');
      }
    } catch {
      notifyError('Failed to save Sales Representative');
    }
    setSavingEmployee(false);
  }

  async function deleteSalesRep(id: string) {
    if (
      !(await confirmDialog('Delete this Sales Representative profile? Assigned accounts will be unlinked.', {
        title: 'Delete Sales Representative',
        confirmLabel: 'Delete',
      }))
    )
      return;
    if (
      !(await confirmDialog('Delete this Sales Representative profile? Assigned accounts will be unlinked.', {
        title: 'Delete Sales Representative',
        confirmLabel: 'Delete',
      }))
    )
      return;
    try {
      const res = await fetch(`/api/ai-employees/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.ok) {
        notifySuccess('Sales Representative deleted.');
        void loadAccounts();
      } else {
        notifyError(data.error ?? 'Failed to delete');
      }
    } catch {
      notifyError('Failed to delete Sales Representative');
    }
  }

  /** Whether the given Sales Rep has at least one email or LinkedIn account assigned. */
  const repHasAccount = (repId: string | null | undefined): boolean =>
    !!repId &&
    (emailAccounts.some(a => a.aiEmployeeId === repId) || linkedinAccounts.some(a => a.aiEmployeeId === repId));

  /** Open the profile editor for a new or existing Sales Rep (resets connect-flow state). */
  function openEmployeeEditor(emp: Partial<SalesRep>) {
    setEditingEmployee(emp);
    setAutoCreatedRepId(null);
  }

  /**
   * Ensure the Sales Rep currently being edited exists in the backend so an
   * account can be assigned to it. A brand-new rep has no id yet, but the OAuth
   * connect flow assigns the account to a rep id — so we persist it first and
   * remember that we auto-created it (for cleanup on cancel).
   */
  async function ensureEditingRepPersisted(): Promise<string | null> {
    if (!editingEmployee) return null;
    if (editingEmployee._id) return editingEmployee._id;
    if (!editingEmployee.name?.trim()) {
      notifyError('Enter a name before connecting an account.');
      return null;
    }
    try {
      const res = await fetch('/api/ai-employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingEmployee),
      });
      const data = await res.json();
      if (data.ok && data.aiEmployee?._id) {
        const id = data.aiEmployee._id as string;
        setEditingEmployee({ ...editingEmployee, _id: id });
        setAutoCreatedRepId(id);
        await loadAccounts();
        return id;
      }
      notifyError(data.error ?? 'Failed to create Sales Representative');
      return null;
    } catch {
      notifyError('Failed to create Sales Representative');
      return null;
    }
  }

  /**
   * Close the profile editor. If we implicitly created a rep for the connect
   * flow but it never got an account assigned (e.g. the user closed the OAuth
   * popup), remove it again so no account-less rep is left behind.
   */
  async function cancelEmployeeEditor() {
    const orphanId = autoCreatedRepId;
    setEditingEmployee(null);
    setAutoCreatedRepId(null);
    if (orphanId && !repHasAccount(orphanId)) {
      try {
        await fetch(`/api/ai-employees/${encodeURIComponent(orphanId)}`, { method: 'DELETE' });
        await loadAccounts();
      } catch {
        /* best-effort cleanup */
      }
    }
  }

  // --- Email OAuth (Instantly) — Popup + Poll ---
  async function assignAccount(provider: string, accountId: string, aiEmployeeId: string | null) {
    try {
      const res = await fetch(`/api/accounts/${provider}/${encodeURIComponent(accountId)}/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aiEmployeeId }),
      });
      const data = await res.json();
      if (data.ok) {
        notifySuccess('Account assignment updated.');
        void loadAccounts();
      } else {
        notifyError(data.error ?? 'Failed to assign account');
      }
    } catch {
      notifyError('Failed to assign account');
    }
  }

  // Mark a Unipile mailbox already-warmed (or undo it). Warming skips the
  // 15→35→75 daily ramp and sends at the full limit, and the server also
  // releases any of this mailbox's mail the old daily cap had pushed to
  // tomorrow. loadAccounts() re-reads so the toggle reflects the stored state
  // rather than an optimistic guess — a failed write leaves the switch as it was.
  async function setMailboxWarmed(accountId: string, warmed: boolean) {
    try {
      const res = await fetch(`/api/accounts/unipile-email/${encodeURIComponent(accountId)}/warmup`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ warmed }),
      });
      const data = await res.json();
      if (data.ok) {
        notifySuccess(
          warmed
            ? `Mailbox marked as warmed up — daily limit ${data.dailyLimit}` +
                (data.releasedJobs ? `, ${data.releasedJobs} queued email(s) released.` : '.')
            : 'Warm-up removed — the mailbox is back on the ramp.',
        );
      } else {
        notifyError(data.error ?? 'Failed to update the warm-up setting');
      }
    } catch {
      notifyError('Failed to update the warm-up setting');
    }
    void loadAccounts();
  }

  // --- Email via Unipile (ADR-0001) — Popup + Poll ---
  //
  // The only email-connect path now: the Instantly connect flow has been
  // retired from the UI as email moves onto Unipile (ADR-0001).
  //
  // `emailProvider` selects Gmail or Outlook and is carried through both the
  // init and the status poll — the server filters the provider's accounts by
  // type, so a poll for the wrong provider would never see the new mailbox.
  async function connectUnipileEmail(forAiEmployee?: RepIdTarget, emailProvider: 'google' | 'microsoft' = 'google') {
    setError('');
    setSuccess('');
    setConnectingEmail(true);
    // Opened before the first await, while the click's user activation is still
    // ours — see oauth-popup.ts. Anything later gets blocked by the browser.
    const popup = openAuthPopup({ message: 'Connecting your Sending Account…' });

    // Attach ONLY the mailbox(es) just connected in this flow to the rep — the
    // ids the server reports as connected — never every unassigned mailbox.
    // Bulk-assigning swept in a mailbox left unassigned by a deleted rep, so one
    // rep ended up holding a different rep's inbox. Returns how many it attached.
    const assignConnectedAccounts = async (connectedIds: string[]): Promise<number> => {
      if (connectedIds.length === 0) return 0;
      const repId = await resolveRepId(forAiEmployee);
      if (!repId) return 0;
      const listed = await fetch('/api/accounts/unipile-email')
        .then(r => r.json())
        .catch(() => ({ ok: false }));
      if (!listed.ok) return 0;
      const justConnected = new Set(connectedIds);
      let assigned = 0;
      for (const acc of listed.accounts ?? []) {
        // Only a just-connected mailbox that is not already on this rep.
        if (justConnected.has(acc.id) && acc.aiEmployeeId !== repId) {
          await assignAccount('unipile_email', acc.id, repId);
          assigned++;
        }
      }
      return assigned;
    };

    try {
      const initRes = await fetch('/api/connect/unipile-email/init', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailProvider }),
      });
      const initData = await initRes.json();
      if (!initData.ok) {
        popup.close();
        notifyError(initData.error ?? 'Failed to start mailbox connection');
        setConnectingEmail(false);
        return;
      }

      if (!(await popup.navigate(initData.url))) {
        setConnectingEmail(false);
        return;
      }

      const pollInterval = setInterval(async () => {
        try {
          const closed = popup.isClosed();
          const statusRes = await fetch(`/api/connect/unipile-email/status?emailProvider=${emailProvider}`);
          const statusData = await statusRes.json();

          // Keep polling until the popup closes or the server reports the flow
          // finished. `completed` is true not only when a NEW mailbox was filed
          // but also when the person reconnected one Unipile already held — the
          // case that used to leave the wizard spinning with nothing to show.
          if (!emailConnectFlowEnded(closed, statusData)) return;

          clearInterval(pollInterval);
          clearTimeout(giveUp);
          if (!closed) popup.close();
          setConnectingEmail(false);

          // Attach ONLY the mailbox(es) the server says were connected in this
          // flow — a freshly filed one, or the existing row a reconnect resolved
          // to (a mailbox left unassigned when its Sales Rep was deleted). Never
          // every unassigned mailbox: that swept a colleague's inbox onto this
          // rep. Then announce success if a mailbox was filed, completed, or
          // just attached.
          const connectedIds: string[] = Array.isArray(statusData.connectedAccountIds)
            ? statusData.connectedAccountIds
            : [];
          const assigned = await assignConnectedAccounts(connectedIds);
          void loadAccounts();
          if (emailConnectSucceeded(statusData, assigned)) {
            notifySuccess('Sending Account connected!');
          }
        } catch {
          // A single failed poll is not a failed connection — the next one
          // answers. The popup closing is what ends this either way.
        }
      }, 2000);

      // A popup left open otherwise polls every two seconds forever. The
      // LinkedIn flow has had this since it was written; this one was copied
      // from it and arrived without it, which is the drift a third copy of a
      // hundred lines was always going to produce.
      const giveUp = setTimeout(
        () => {
          clearInterval(pollInterval);
          setConnectingEmail(false);
        },
        5 * 60 * 1000,
      );
    } catch {
      popup.close();
      notifyError('Failed to connect Sending Account');
      setConnectingEmail(false);
    }
  }

  // --- LinkedIn OAuth (Unipile) — Popup + Poll ---
  async function connectLinkedin(forAiEmployee?: RepIdTarget) {
    setError('');
    setSuccess('');
    setConnectingLinkedin(true);
    // Opened before the first await — see connectEmail above.
    const popup = openAuthPopup({ message: 'Connecting your LinkedIn account…' });
    try {
      const initRes = await fetch('/api/connect/linkedin/init', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const initData = await initRes.json();
      if (!initData.ok) {
        popup.close();
        notifyError(initData.error ?? 'Failed to start LinkedIn connection');
        setConnectingLinkedin(false);
        return;
      }

      if (!(await popup.navigate(initData.url))) {
        setConnectingLinkedin(false);
        return;
      }

      // Attach ONLY the account(s) the server says were connected in this flow —
      // never every unassigned LinkedIn account. Bulk-assigning swept a colleague's
      // account onto this rep, the same bug the email flow had. Returns the count.
      const assignConnectedLinkedin = async (connectedIds: string[]): Promise<number> => {
        if (connectedIds.length === 0) return 0;
        const repId = await resolveRepId(forAiEmployee);
        if (!repId) return 0;
        const linkedinRes = await fetch('/api/accounts/linkedin')
          .then(r => r.json())
          .catch(() => ({ ok: false }));
        if (!linkedinRes.ok) return 0;
        const justConnected = new Set(connectedIds);
        let assigned = 0;
        for (const acc of linkedinRes.accounts ?? []) {
          if (justConnected.has(acc.id) && acc.aiEmployeeId !== repId) {
            await assignAccount('unipile_linkedin', acc.id, repId);
            assigned++;
          }
        }
        return assigned;
      };

      // Whether the flow is done — a new account filed, or a reconnect the server
      // completed (which files nothing new, since a reconnected account is the
      // same person under a new provider id). Assigns the resolved account and
      // announces success. `completed` is the reconnect signal, mirroring email.
      const finishIfConnected = async (statusData: {
        ok?: boolean;
        newAccountsFound?: number;
        completed?: boolean;
        connectedAccountIds?: string[];
      }): Promise<boolean> => {
        const done = !!statusData.ok && ((statusData.newAccountsFound ?? 0) > 0 || statusData.completed === true);
        if (!done) return false;
        const connectedIds = Array.isArray(statusData.connectedAccountIds) ? statusData.connectedAccountIds : [];
        const assigned = await assignConnectedLinkedin(connectedIds);
        if ((statusData.newAccountsFound ?? 0) > 0 || statusData.completed === true || assigned > 0) {
          notifySuccess('LinkedIn account connected!');
        }
        void loadAccounts();
        return true;
      };

      const pollInterval = setInterval(async () => {
        try {
          const closed = popup.isClosed();
          const statusRes = await fetch('/api/connect/linkedin/status');
          const statusData = await statusRes.json();
          const finished = await finishIfConnected(statusData);

          if (!closed && !finished) return;

          clearInterval(pollInterval);
          clearTimeout(giveUp);
          if (!closed) popup.close();
          setConnectingLinkedin(false);
        } catch {
          /* A single failed poll is not a failed connection — the next answers. */
        }
      }, 3000);

      const giveUp = setTimeout(
        () => {
          clearInterval(pollInterval);
          setConnectingLinkedin(false);
        },
        5 * 60 * 1000,
      );
    } catch (err) {
      popup.close();
      notifyError(err instanceof Error ? err.message : 'Connection failed');
      setConnectingLinkedin(false);
    }
  }

  // --- Disconnect ---
  async function disconnectAccount(provider: string, accountId: string) {
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`/api/accounts/${provider}/${encodeURIComponent(accountId)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.ok) {
        notifySuccess('Account disconnected.');
        void loadAccounts();
      } else {
        notifyError(data.error ?? 'Failed to disconnect');
      }
    } catch {
      notifyError('Failed to disconnect account');
    }
  }

  // --- HubSpot OAuth (popup + localStorage callback) ---
  async function connectHubspot() {
    setError('');
    setSuccess('');
    setConnectingCrm(true);
    // Opened before the first await — see connectEmail above.
    const popup = openAuthPopup({ message: 'Connecting HubSpot…', width: 720, height: 820 });
    try {
      const redirectUri = `${window.location.origin}/auth/hubspot/callback`;
      const initRes = await fetch(`/api/crm/hubspot/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`);
      const initData = await initRes.json();
      if (!initData.ok) {
        popup.close();
        notifyError(initData.error ?? 'Failed to start HubSpot connection');
        setConnectingCrm(false);
        return;
      }

      // Clear any stale callback before handing the popup to HubSpot
      localStorage.removeItem('hubspot_oauth_callback');

      if (!(await popup.navigate(initData.url))) {
        setConnectingCrm(false);
        return;
      }

      const startedAt = Date.now();

      const poll = setInterval(async () => {
        try {
          const raw = localStorage.getItem('hubspot_oauth_callback');
          if (raw) {
            clearInterval(poll);
            popup.close();
            localStorage.removeItem('hubspot_oauth_callback');
            const parsed = JSON.parse(raw) as { code?: string; error?: string; errorDescription?: string };
            if (parsed.error || !parsed.code) {
              notifyError(parsed.errorDescription ?? parsed.error ?? 'HubSpot authorization was cancelled');
              setConnectingCrm(false);
              return;
            }
            const cbRes = await fetch('/api/crm/hubspot/callback', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: parsed.code, redirectUri }),
            });
            const cbData = await cbRes.json();
            if (cbData.ok) {
              notifySuccess(`HubSpot connected (${cbData.accountLabel ?? cbData.accountId ?? ''})`);
              void loadAccounts();
            } else {
              notifyError(cbData.error ?? 'HubSpot connection failed');
            }
            setConnectingCrm(false);
            return;
          }
          if (popup.isClosed() || Date.now() - startedAt > 5 * 60 * 1000) {
            clearInterval(poll);
            setConnectingCrm(false);
          }
        } catch {
          /* keep polling */
        }
      }, 800);
    } catch (err) {
      popup.close();
      notifyError(err instanceof Error ? err.message : 'HubSpot connection failed');
      setConnectingCrm(false);
    }
  }

  async function disconnectCrm(connectionId: string, label: string) {
    if (
      !(await confirmDialog(
        `Disconnect ${label}? Existing CRM mappings remain in place but no further updates will be pushed.`,
        { title: 'Disconnect CRM', confirmLabel: 'Disconnect' },
      ))
    )
      return;
    try {
      const ok = await disconnectCrmConnection(connectionId)(undefined);
      if (ok) {
        notifySuccess('CRM disconnected.');
        void loadAccounts();
      } else {
        notifyError('Connection not found.');
      }
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Failed to disconnect CRM');
    }
  }

  // --- HubSpot Lead Status mapping (Teil 2) ---------------------------------
  const MAPPING_FIELDS = {
    connectionId: 1,
    optionsReadable: 1,
    readError: 1,
    aiSuggested: 1,
    hasSaved: 1,
    options: { value: 1, label: 1 },
    rows: { localStatus: 1, localLabel: 1, savedValue: 1, suggestedValue: 1 },
  } as const;

  const seedMappingDraft = (view: HubspotLeadStatusMappingView) => {
    const draft: Record<string, string> = {};
    for (const r of view.rows ?? []) draft[r.localStatus] = r.savedValue || r.suggestedValue || '';
    setMappingDraft(draft);
  };

  async function toggleMapping(connectionId: string) {
    if (mappingConnId === connectionId) {
      setMappingConnId(null);
      setMappingView(null);
      return;
    }
    setMappingConnId(connectionId);
    setMappingView(null);
    setMappingError(null);
    setMappingBusy('load');
    try {
      const view = await getHubspotLeadStatusMapping(connectionId)(MAPPING_FIELDS);
      setMappingView(view);
      seedMappingDraft(view);
    } catch (err) {
      setMappingError(err instanceof Error ? err.message : 'Failed to load the mapping');
    } finally {
      setMappingBusy('');
    }
  }

  async function suggestMapping() {
    if (!mappingConnId) return;
    setMappingBusy('suggest');
    setMappingError(null);
    try {
      const view = await suggestHubspotLeadStatusMapping(mappingConnId)(MAPPING_FIELDS);
      setMappingView(view);
      const draft: Record<string, string> = {};
      for (const r of view.rows ?? []) draft[r.localStatus] = r.suggestedValue || '';
      setMappingDraft(draft);
      notifySuccess(view.aiSuggested ? 'AI suggestion applied — review and save.' : 'Suggestion applied — review and save.');
    } catch (err) {
      setMappingError(err instanceof Error ? err.message : 'Failed to suggest a mapping');
    } finally {
      setMappingBusy('');
    }
  }

  async function saveMapping() {
    if (!mappingConnId) return;
    setMappingBusy('save');
    setMappingError(null);
    try {
      await saveHubspotLeadStatusMapping(mappingConnId, JSON.stringify(mappingDraft))(undefined);
      notifySuccess('Lead Status mapping saved.');
      const view = await getHubspotLeadStatusMapping(mappingConnId)(MAPPING_FIELDS);
      setMappingView(view);
      seedMappingDraft(view);
    } catch (err) {
      setMappingError(err instanceof Error ? err.message : 'Failed to save the mapping');
    } finally {
      setMappingBusy('');
    }
  }

  const renderMappingPanel = () => html`
    <div class="border-base-300 bg-base-100 mt-2 rounded-lg border p-3">
      <div class="mb-1 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <span class="icon-[material-symbols--rule] text-warning text-sm"></span>
          <span class="text-sm font-medium">Lead Status mapping</span>
          ${
            mappingView?.hasSaved
              ? html`<span class="badge badge-success badge-xs gap-1">
                  <span class="icon-[material-symbols--check-circle] text-[0.65rem]"></span>Saved
                </span>`
              : nothing
          }
        </div>
        ${
          mappingView?.optionsReadable
            ? html`<button
                class="btn btn-ghost btn-xs gap-1"
                ?disabled=${mappingBusy !== ''}
                title="Let AI propose a mapping from this portal's option labels"
                @click=${() => void suggestMapping()}
              >
                ${
                  mappingBusy === 'suggest'
                    ? html`<span class="loading loading-spinner loading-xs"></span>`
                    : html`<span class="icon-[material-symbols--auto-awesome] text-sm"></span>`
                }
                Suggest with AI
              </button>`
            : nothing
        }
      </div>
      <p class="text-base-content/50 mb-3 text-xs">
        Choose which HubSpot Lead Status each of our states writes when a lead is pushed. Leave a state on “Don't
        set” to skip it.
      </p>
      ${
        mappingBusy === 'load' && !mappingView
          ? html`<div class="text-base-content/50 flex items-center gap-2 py-2 text-xs">
              <span class="loading loading-spinner loading-xs"></span> Loading the portal's options…
            </div>`
          : mappingError
            ? html`<div class="alert alert-error px-3 py-2 text-xs" role="alert">${mappingError}</div>`
            : mappingView && !mappingView.optionsReadable
              ? html`<div class="border-warning/40 bg-warning/10 text-warning rounded-md border px-3 py-2 text-xs">
                  <div class="font-medium">Couldn't read this portal's Lead Status options.</div>
                  ${mappingView.readError ? html`<div class="mt-0.5 opacity-80">${mappingView.readError}</div>` : nothing}
                  <div class="mt-1 opacity-80">
                    The connection likely needs the property-read scope — grant it and reconnect, then reopen this
                    panel.
                  </div>
                </div>`
              : mappingView
                ? html`
                    <div class="flex flex-col gap-2">
                      ${(mappingView.rows ?? []).map(
                        row => html`
                          <div class="flex items-center gap-2">
                            <span class="badge badge-ghost badge-sm w-24 shrink-0 justify-start font-medium">
                              ${row.localLabel}
                            </span>
                            <span
                              class="icon-[material-symbols--arrow-forward] text-base-content/30 shrink-0 text-sm"
                            ></span>
                            <select
                              class="select select-bordered select-sm min-w-0 flex-1"
                              @change=${(e: Event) =>
                                setMappingDraft({
                                  ...mappingDraft,
                                  [row.localStatus]: (e.target as HTMLSelectElement).value,
                                })}
                            >
                              <option value="" ?selected=${!mappingDraft[row.localStatus]}>— Don't set —</option>
                              ${(mappingView?.options ?? []).map(
                                o => html`<option value=${o.value} ?selected=${mappingDraft[row.localStatus] === o.value}>
                                  ${o.label}
                                </option>`,
                              )}
                            </select>
                          </div>
                        `,
                      )}
                    </div>
                    <div class="mt-3 flex items-center justify-between gap-2">
                      <span class="text-base-content/40 text-xs">
                        ${
                          mappingView.aiSuggested
                            ? 'AI-suggested — review before saving.'
                            : mappingView.hasSaved
                              ? nothing
                              : 'Prefilled from a best-guess match.'
                        }
                      </span>
                      <button
                        class="btn btn-warning btn-sm gap-1"
                        ?disabled=${mappingBusy !== ''}
                        @click=${() => void saveMapping()}
                      >
                        ${mappingBusy === 'save' ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing}
                        Save mapping
                      </button>
                    </div>
                  `
                : nothing
      }
    </div>
  `;

  return html`
    <div class="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <div class="flex items-center gap-3">
        <span class="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
          <span class="icon-[material-symbols--settings] text-2xl"></span>
        </span>
        <div>
          <h1 class="text-3xl font-bold">Settings</h1>
          <p class="text-base-content/70">Manage your Sales Representatives and the sending accounts they use.</p>
        </div>
      </div>

      ${error && !loading ? html`<div class="alert alert-error text-sm" role="alert">${error}</div>` : nothing}

      <!-- Sales Representative Profiles -->
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="bg-accent/10 text-accent flex h-10 w-10 items-center justify-center rounded-lg">
                <span class="icon-[material-symbols--smart-toy] text-xl"></span>
              </span>
              <div>
                <h2 class="card-title text-lg">Sales Representatives</h2>
                <p class="text-base-content/70 text-sm">
                  Each Sales Representative has its own persona and sending accounts.
                </p>
              </div>
            </div>
            <button
              class="btn btn-accent btn-sm gap-1"
              @click=${() =>
                openEmployeeEditor({
                  name: '',
                  role: '',
                  company: '',
                  communicationStyle: '',
                  signOff: '',
                  linkedinSignOff: '',
                  isDefault: false,
                })}
            >
              <span class="icon-[material-symbols--add] text-lg"></span> New Sales Representative
            </button>
          </div>

          ${
            loading
              ? html`<div class="mt-4 flex flex-col gap-2">
                ${skeletonLine({ width: 'w-full', height: 'h-14' })}
                ${skeletonLine({ width: 'w-full', height: 'h-14' })}
              </div>`
              : salesReps.length === 0
                ? html`<div class="border-base-300 bg-base-200/50 mt-4 rounded-lg border border-dashed p-6 text-center">
                  <p class="text-base-content/60 text-sm">
                    No Sales Representatives yet. Each Sales Representative is a sender persona that owns email and
                    LinkedIn accounts.
                  </p>
                  <button
                    class="btn btn-accent btn-sm mt-3 gap-1"
                    @click=${() =>
                      openEmployeeEditor({
                        name: '',
                        role: '',
                        company: '',
                        communicationStyle: '',
                        signOff: '',
                        isDefault: false,
                      })}
                  >
                    <span class="icon-[material-symbols--add] text-base" aria-hidden="true"></span>
                    Create your first Sales Representative
                  </button>
                </div>`
                : html`<div class="mt-4 flex flex-col gap-3">
                  ${salesReps.map(emp => {
                    const isExpanded = expandedEmployeeId === emp._id;
                    const assignedEmails = emailAccounts.filter(a => a.aiEmployeeId === emp._id);
                    const assignedLinkedin = linkedinAccounts.filter(a => a.aiEmployeeId === emp._id);
                    return html`
                      <div
                        class="${
                          isExpanded ? 'border-accent/50 bg-base-200/30' : 'border-base-300'
                        } rounded-lg border transition"
                      >
                        <!-- Header row — clickable to expand -->
                        <div
                          class="flex cursor-pointer items-center justify-between p-4"
                          @click=${() => setExpandedEmployeeId(isExpanded ? null : emp._id)}
                        >
                          <div class="flex items-center gap-3">
                            <span class="icon-[material-symbols--smart-toy] text-accent text-lg"></span>
                            <div>
                              <div class="flex items-center gap-2">
                                <span class="font-medium">${emp.name}</span>
                                ${
                                  emp.isDefault
                                    ? html`<span class="badge badge-accent badge-sm">Default</span>`
                                    : nothing
                                }
                              </div>
                              <div class="text-base-content/60 text-sm">${emp.role} at ${emp.company}</div>
                            </div>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="text-base-content/50 flex gap-1 text-xs">
                              ${
                                assignedEmails.length > 0
                                  ? html`<span class="badge badge-ghost badge-xs gap-1"
                                    ><span class="icon-[material-symbols--mail] text-xs"></span
                                    >${assignedEmails.length}</span
                                  >`
                                  : nothing
                              }
                              ${
                                assignedLinkedin.length > 0
                                  ? html`<span class="badge badge-ghost badge-xs gap-1"
                                    ><span class="icon-[material-symbols--share] text-xs"></span
                                    >${assignedLinkedin.length}</span
                                  >`
                                  : nothing
                              }
                            </div>
                            <span
                              class="icon-[material-symbols--${
                                isExpanded ? 'expand-less' : 'expand-more'
                              }] text-base-content/40 text-xl"
                            ></span>
                          </div>
                        </div>

                        <!-- Expanded content -->
                        ${
                          isExpanded
                            ? html`
                              <div class="border-base-300 border-t px-6 pt-4 pb-6">
                                <!-- Actions -->
                                <div class="mb-4 flex gap-2">
                                  <button
                                    class="btn btn-ghost btn-sm gap-1"
                                    @click=${() => openEmployeeEditor({ ...emp })}
                                  >
                                    <span class="icon-[material-symbols--edit] text-base"></span> Edit Profile
                                  </button>
                                  <button
                                    class="btn btn-ghost btn-sm text-error gap-1"
                                    @click=${() => void deleteSalesRep(emp._id)}
                                  >
                                    <span class="icon-[material-symbols--delete-outline] text-base"></span> Delete
                                  </button>
                                </div>

                                <!-- Email Accounts -->
                                <div class="mb-4">
                                  <div class="mb-2 flex items-center justify-between">
                                    <h3 class="flex items-center gap-2 text-base font-semibold">
                                      <span class="icon-[material-symbols--mail] text-secondary text-lg"></span> Email
                                      Accounts
                                    </h3>
                                    <div class="dropdown dropdown-end">
                                      <label
                                        tabindex="0"
                                        class="btn btn-ghost btn-sm gap-1"
                                        ?disabled=${connectingEmail}
                                      >
                                        ${
                                          connectingEmail
                                            ? html`<span class="loading loading-spinner loading-xs"></span>`
                                            : html`<span class="icon-[material-symbols--add] text-base"></span> Add Email`
                                        }
                                      </label>
                                      <ul
                                        tabindex="0"
                                        class="dropdown-content menu rounded-box bg-base-100 z-10 w-48 p-2 shadow"
                                      >
                                        <li>
                                          <a @click=${() => void connectUnipileEmail(emp._id, 'google')}
                                            >Gmail via Unipile</a
                                          >
                                        </li>
                                        <li>
                                          <a @click=${() => void connectUnipileEmail(emp._id, 'microsoft')}
                                            >Outlook via Unipile</a
                                          >
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                  ${
                                    assignedEmails.length === 0
                                      ? html`<div
                                        class="border-base-300 text-base-content/40 rounded-lg border border-dashed p-3 text-center text-xs"
                                      >
                                        No email accounts assigned.
                                      </div>`
                                      : html`<div class="flex flex-col gap-1.5">
                                        ${assignedEmails.map(
                                          acc => html`
                                            <div
                                              class="bg-base-200/50 flex items-center justify-between rounded-lg px-4 py-3"
                                            >
                                              <div class="flex items-center gap-3">
                                                <span
                                                  class="icon-[material-symbols--mail] text-secondary text-base"
                                                ></span>
                                                <div>
                                                  <div class="text-sm font-medium">${acc.name}</div>
                                                  <div class="text-base-content/50 text-xs">${acc.email}</div>
                                                </div>
                                              </div>
                                              <div class="flex items-center gap-4">
                                                ${
                                                  acc.provider === 'unipile_email'
                                                    ? html`<label
                                                        class="flex cursor-pointer items-center gap-2"
                                                        title="An already-warmed-up mailbox skips the 15 → 35 → 75 daily ramp and sends at the full daily limit."
                                                      >
                                                        <div class="text-right">
                                                          <div class="text-xs font-medium">Warmed up</div>
                                                          <div class="text-base-content/40 text-xs">
                                                            ${acc.emailWarmedManually
                                                              ? `${acc.dailyLimit ?? 100}/day`
                                                              : `ramp · ${acc.dailyLimit ?? 15}/day`}
                                                          </div>
                                                        </div>
                                                        <input
                                                          type="checkbox"
                                                          class="toggle toggle-success toggle-sm"
                                                          .checked=${acc.emailWarmedManually === true}
                                                          @change=${(e: Event) =>
                                                            void setMailboxWarmed(
                                                              acc.email,
                                                              (e.target as HTMLInputElement).checked,
                                                            )}
                                                        />
                                                      </label>`
                                                    : ''
                                                }
                                                <div class="text-right">
                                                  <div class="text-secondary text-lg font-bold">
                                                    ${(acc.totalSent ?? 0).toLocaleString()}
                                                  </div>
                                                  <div class="text-base-content/40 text-xs">E-Mails Sent</div>
                                                </div>
                                                <button
                                                  class="btn btn-ghost btn-xs text-error"
                                                  title="Remove from this employee"
                                                  @click=${() => void disconnectAccount(acc.provider ?? 'instantly_email', acc.email)}
                                                >
                                                  <span class="icon-[material-symbols--close] text-sm"></span>
                                                </button>
                                              </div>
                                            </div>
                                          `,
                                        )}
                                      </div>`
                                  }
                                </div>

                                <!-- LinkedIn Accounts -->
                                <div>
                                  <div class="mb-2 flex items-center justify-between">
                                    <h3 class="flex items-center gap-2 text-base font-semibold">
                                      <span class="icon-[material-symbols--share] text-info text-lg"></span> LinkedIn
                                      Accounts
                                    </h3>
                                    <button
                                      class="btn btn-ghost btn-sm gap-1"
                                      ?disabled=${connectingLinkedin}
                                      @click=${() => void connectLinkedin(emp._id)}
                                    >
                                      ${
                                        connectingLinkedin
                                          ? html`<span class="loading loading-spinner loading-xs"></span>`
                                          : html`<span class="icon-[material-symbols--add] text-base"></span> Add LinkedIn`
                                      }
                                    </button>
                                  </div>
                                  ${
                                    assignedLinkedin.length === 0
                                      ? html`<div
                                        class="border-base-300 text-base-content/40 rounded-lg border border-dashed p-3 text-center text-xs"
                                      >
                                        No LinkedIn accounts assigned.
                                      </div>`
                                      : html`<div class="flex flex-col gap-1.5">
                                        ${assignedLinkedin.map(
                                          acc => html`
                                            <div
                                              class="bg-base-200/50 flex items-center justify-between rounded-lg px-4 py-3"
                                            >
                                              <div class="flex items-center gap-3">
                                                <span class="icon-[material-symbols--share] text-info text-base"></span>
                                                <div>
                                                  <div class="flex items-center gap-2">
                                                    <span class="text-sm font-medium">${acc.name}</span>
                                                    ${(() => {
                                                      // The seat decides whether invitations from this
                                                      // account can carry a personal note. Shown here
                                                      // because this is where an operator looks at an
                                                      // account as an account.
                                                      const seat = describeSeat(acc.premiumFeatures);
                                                      return html`<span
                                                        class=${seatBadgeClass(seat.kind)}
                                                        title=${seat.title}
                                                        >${seat.label}</span
                                                      >`;
                                                    })()}
                                                  </div>
                                                  <div class="text-base-content/50 text-xs">
                                                    ${acc.status ?? 'connected'}
                                                  </div>
                                                </div>
                                              </div>
                                              <div class="flex items-center gap-4">
                                                <div class="text-right">
                                                  <div class="text-info text-lg font-bold">
                                                    ${(acc.totalInvitesSent ?? 0).toLocaleString()}
                                                  </div>
                                                  <div class="text-base-content/40 text-xs">Invites</div>
                                                </div>
                                                <div class="text-right">
                                                  <div class="text-info text-lg font-bold">
                                                    ${(acc.totalDmsSent ?? 0).toLocaleString()}
                                                  </div>
                                                  <div class="text-base-content/40 text-xs">DMs</div>
                                                </div>
                                                <button
                                                  class="btn btn-ghost btn-xs text-error"
                                                  title="Remove from this employee"
                                                  @click=${() => void disconnectAccount('unipile_linkedin', acc.id)}
                                                >
                                                  <span class="icon-[material-symbols--close] text-sm"></span>
                                                </button>
                                              </div>
                                            </div>
                                          `,
                                        )}
                                      </div>`
                                  }
                                </div>
                              </div>
                            `
                            : nothing
                        }
                      </div>
                    `;
                  })}
                </div>`
          }
          ${
            editingEmployee !== null
              ? html`
                <div class="border-base-300 bg-base-200 mt-4 rounded-lg border p-4">
                  <h3 class="mb-3 font-semibold">
                    ${editingEmployee._id && editingEmployee._id !== autoCreatedRepId ? 'Edit Profile' : 'New Profile'}
                  </h3>
                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label class="form-control w-full">
                      <div class="label"><span class="label-text">Name</span></div>
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        placeholder="Sarah Miller"
                        .value=${editingEmployee.name ?? ''}
                        @input=${(e: InputEvent) =>
                          setEditingEmployee({ ...editingEmployee, name: (e.target as HTMLInputElement).value })}
                      />
                    </label>
                    <label class="form-control w-full">
                      <div class="label"><span class="label-text">Role</span></div>
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        placeholder="Business Development Rep"
                        .value=${editingEmployee.role ?? ''}
                        @input=${(e: InputEvent) =>
                          setEditingEmployee({ ...editingEmployee, role: (e.target as HTMLInputElement).value })}
                      />
                    </label>
                    <label class="form-control w-full">
                      <div class="label"><span class="label-text">Company</span></div>
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        placeholder="New Digital Intelligence"
                        .value=${editingEmployee.company ?? ''}
                        @input=${(e: InputEvent) =>
                          setEditingEmployee({ ...editingEmployee, company: (e.target as HTMLInputElement).value })}
                      />
                    </label>
                    <label class="form-control w-full">
                      <div class="label"><span class="label-text">Communication Style</span></div>
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        placeholder="professional but warm, concise"
                        .value=${editingEmployee.communicationStyle ?? ''}
                        @input=${(e: InputEvent) =>
                          setEditingEmployee({
                            ...editingEmployee,
                            communicationStyle: (e.target as HTMLInputElement).value,
                          })}
                      />
                    </label>
                  </div>
                  <label class="form-control mt-3 w-full">
                    <div class="label"><span class="label-text">Email Sign-Off</span></div>
                    <textarea
                      class="textarea textarea-bordered w-full"
                      rows="3"
                      placeholder="Best regards,&#10;Sarah Miller&#10;Business Development"
                      .value=${editingEmployee.signOff ?? ''}
                      @input=${(e: InputEvent) =>
                        setEditingEmployee({ ...editingEmployee, signOff: (e.target as HTMLTextAreaElement).value })}
                    ></textarea>
                  </label>
                  <label class="form-control mt-3 w-full">
                    <div class="label"><span class="label-text">LinkedIn DM Sign-Off</span></div>
                    <p class="text-base-content/60 mb-1 text-xs leading-snug">
                      Used on LinkedIn messages. Leave empty to reuse the email sign-off. Connection requests
                      never carry a sign-off.
                    </p>
                    <textarea
                      class="textarea textarea-bordered w-full"
                      rows="2"
                      placeholder="– Sarah"
                      .value=${editingEmployee.linkedinSignOff ?? ''}
                      @input=${(e: InputEvent) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          linkedinSignOff: (e.target as HTMLTextAreaElement).value,
                        })}
                    ></textarea>
                  </label>
                  <div class="border-base-300 mt-3 rounded-lg border border-dashed p-3">
                    <div class="mb-2 flex items-center justify-between gap-2">
                      <span class="text-sm font-medium">Sending Accounts</span>
                      <div class="flex gap-2">
                        <div class="dropdown dropdown-end">
                          <label tabindex="0" class="btn btn-ghost btn-xs gap-1" ?disabled=${connectingEmail}>
                            ${
                              connectingEmail
                                ? html`<span class="loading loading-spinner loading-xs"></span>`
                                : html`<span class="icon-[material-symbols--mail] text-sm"></span> Add Email`
                            }
                          </label>
                          <ul
                            tabindex="0"
                            class="dropdown-content menu rounded-box bg-base-100 z-10 w-48 p-2 shadow"
                          >
                            <li>
                              <a
                                @click=${() => {
                                  if (!editingEmployee.name?.trim()) {
                                    notifyError('Enter a name before connecting an account.');
                                    return;
                                  }
                                  void connectUnipileEmail(ensureEditingRepPersisted, 'google');
                                }}
                                >Gmail via Unipile</a
                              >
                            </li>
                            <li>
                              <a
                                @click=${() => {
                                  if (!editingEmployee.name?.trim()) {
                                    notifyError('Enter a name before connecting an account.');
                                    return;
                                  }
                                  void connectUnipileEmail(ensureEditingRepPersisted, 'microsoft');
                                }}
                                >Outlook via Unipile</a
                              >
                            </li>
                          </ul>
                        </div>
                        <button
                          class="btn btn-ghost btn-xs gap-1"
                          ?disabled=${connectingLinkedin}
                          @click=${() => {
                            if (!editingEmployee.name?.trim()) {
                              notifyError('Enter a name before connecting an account.');
                              return;
                            }
                            void connectLinkedin(ensureEditingRepPersisted);
                          }}
                        >
                          ${
                            connectingLinkedin
                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                              : html`<span class="icon-[material-symbols--share] text-sm"></span> Add LinkedIn`
                          }
                        </button>
                      </div>
                    </div>
                    ${(() => {
                      const repId = editingEmployee._id;
                      const emails = repId ? emailAccounts.filter(a => a.aiEmployeeId === repId) : [];
                      const linkedins = repId ? linkedinAccounts.filter(a => a.aiEmployeeId === repId) : [];
                      if (emails.length === 0 && linkedins.length === 0) {
                        return html`
                          <div class="alert alert-warning px-3 py-2 text-xs">
                            <span class="icon-[material-symbols--warning] text-base"></span>
                            Connect at least one email or LinkedIn account to create this Sales Representative.
                          </div>`;
                      }
                      return html`
                        <div class="flex flex-wrap gap-1.5">
                          ${emails.map(
                            acc => html`<span class="badge badge-secondary badge-sm gap-1"
                              ><span class="icon-[material-symbols--mail] text-xs"></span>${acc.email}</span
                            >`,
                          )}
                          ${linkedins.map(
                            acc => html`<span class="badge badge-info badge-sm gap-1"
                              ><span class="icon-[material-symbols--share] text-xs"></span>${acc.name}</span
                            >`,
                          )}
                        </div>`;
                    })()}
                  </div>
                  <div class="mt-3 flex items-center gap-4">
                    <label class="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        .checked=${editingEmployee.isDefault ?? false}
                        @change=${(e: Event) =>
                          setEditingEmployee({ ...editingEmployee, isDefault: (e.target as HTMLInputElement).checked })}
                      />
                      <span class="text-sm">Set as Default</span>
                    </label>
                    <div class="flex-1"></div>
                    <button class="btn btn-ghost btn-sm" @click=${() => void cancelEmployeeEditor()}>Cancel</button>
                    <button
                      class="btn btn-accent btn-sm"
                      ?disabled=${savingEmployee || !repHasAccount(editingEmployee._id)}
                      title=${
                        repHasAccount(editingEmployee._id)
                          ? ''
                          : 'Connect at least one email or LinkedIn account first.'
                      }
                      @click=${() => void saveSalesRep()}
                    >
                      ${savingEmployee ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing} Save
                    </button>
                  </div>
                </div>
              `
              : nothing
          }
        </div>
      </div>

      <!-- CRM Integrations -->
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="bg-warning/10 text-warning flex h-10 w-10 items-center justify-center rounded-lg">
                <span class="icon-[material-symbols--hub] text-xl"></span>
              </span>
              <div>
                <h2 class="card-title text-lg">CRM Integrations</h2>
                <p class="text-base-content/70 text-sm">
                  Sync contacted leads, replies and activities to your CRM. Pushes happen asynchronously in the
                  background.
                </p>
              </div>
            </div>
          </div>

          <div class="mt-4 flex flex-col gap-3">
            <!-- HubSpot row -->
            <div class="border-base-300 bg-base-200/30 flex items-center justify-between rounded-lg border px-4 py-3">
              <div class="flex items-center gap-3">
                <span class="bg-warning/10 text-warning flex h-9 w-9 items-center justify-center rounded-lg">
                  <span class="icon-[material-symbols--hub] text-lg"></span>
                </span>
                <div>
                  <div class="font-medium">HubSpot</div>
                  ${
                    crmConfig.hubspot.enabled
                      ? crmConfig.hubspot.mode === 'private_app'
                        ? html`<div class="text-base-content/60 text-xs">
                          Configured via server-side Private App token. No user action required.
                        </div>`
                        : html`<div class="text-base-content/60 text-xs">
                          Push contacts and activity notes to a HubSpot portal.
                        </div>`
                      : html`<div class="text-warning text-xs">
                        Not configured on the server. Set <code>HUBSPOT_PRIVATE_APP_TOKEN</code> (recommended) or
                        <code>HUBSPOT_CLIENT_ID</code> + <code>HUBSPOT_CLIENT_SECRET</code> for OAuth.
                      </div>`
                  }
                </div>
              </div>
              ${
                crmConfig.hubspot.mode === 'private_app'
                  ? html`<div class="flex items-center gap-2">
                    <span class="badge badge-success gap-1"
                      ><span class="icon-[material-symbols--check-circle] text-sm"></span>Active</span
                    >
                    ${
                      crmConfig.hubspot.oauthAvailable
                        ? html`<button
                          class="btn btn-outline btn-warning btn-xs gap-1"
                          ?disabled=${connectingCrm}
                          title="Additionally connect a per-user HubSpot account via OAuth (multi-tenant)"
                          @click=${() => void connectHubspot()}
                        >
                          ${
                            connectingCrm
                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                              : html`<span class="icon-[material-symbols--add-link] text-sm"></span>`
                          }
                          Connect another
                        </button>`
                        : nothing
                    }
                  </div>`
                  : html`<button
                    class="btn btn-warning btn-sm gap-1"
                    ?disabled=${!crmConfig.hubspot.enabled || connectingCrm}
                    @click=${() => void connectHubspot()}
                  >
                    ${
                      connectingCrm
                        ? html`<span class="loading loading-spinner loading-xs"></span>`
                        : html`<span class="icon-[material-symbols--add-link] text-base"></span>`
                    }
                    Connect HubSpot
                  </button>`
              }
            </div>

            <!-- Active connections list -->
            ${
              crmConnections.length === 0
                ? html`<div
                  class="border-base-300 text-base-content/40 rounded-lg border border-dashed p-3 text-center text-xs"
                >
                  No CRM connected yet.
                </div>`
                : html`<div class="flex flex-col gap-1.5">
                  ${crmConnections.map(
                    conn => html`
                      <div class="bg-base-200/50 rounded-lg px-4 py-3">
                        <div class="flex items-center justify-between">
                          <div class="flex items-center gap-3">
                            <span class="icon-[material-symbols--hub] text-warning text-base"></span>
                            <div>
                              <div class="text-sm font-medium">
                                ${conn.provider === 'hubspot' ? 'HubSpot' : conn.provider} ·
                                ${conn.accountLabel ?? conn.accountId ?? ''}${
                                  conn.isSystem
                                    ? html` <span class="badge badge-ghost badge-xs ml-1">system</span>`
                                    : nothing
                                }
                              </div>
                              <div class="text-base-content/50 text-xs">
                                Status:
                                ${conn.status}${
                                  conn.lastUsedAt
                                    ? html` · last used ${new Date(conn.lastUsedAt).toLocaleString()}`
                                    : nothing
                                }
                              </div>
                              ${conn.lastError ? html`<div class="text-error text-xs">${conn.lastError}</div>` : nothing}
                            </div>
                          </div>
                          <div class="flex items-center gap-1">
                            ${
                              conn.provider === 'hubspot'
                                ? html`<button
                                  class="btn btn-ghost btn-xs gap-1"
                                  title="Map our lead states to this portal's Lead Status options"
                                  @click=${() => void toggleMapping(conn._id)}
                                >
                                  <span class="icon-[material-symbols--rule] text-sm"></span>
                                  Lead Status
                                  <span
                                    class="icon-[material-symbols--expand-more] text-sm transition-transform ${
                                      mappingConnId === conn._id ? 'rotate-180' : ''
                                    }"
                                  ></span>
                                </button>`
                                : nothing
                            }
                            ${
                              conn.isSystem
                                ? html`<span class="text-base-content/40 text-xs" title="Managed via server env"
                                  >env-managed</span
                                >`
                                : html`<button
                                  class="btn btn-ghost btn-xs text-error"
                                  title="Disconnect"
                                  @click=${() => void disconnectCrm(conn._id, conn.accountLabel ?? conn.provider)}
                                >
                                  <span class="icon-[material-symbols--close] text-sm"></span>
                                </button>`
                            }
                          </div>
                        </div>
                        ${mappingConnId === conn._id ? renderMappingPanel() : nothing}
                      </div>
                    `,
                  )}
                </div>`
            }
            <!-- Locked / coming-soon CRM providers (UI only) -->
            ${lockedCrmProviders.map(
              prov => html`
                <div
                  class="group border-base-300 bg-base-200/20 relative cursor-not-allowed overflow-hidden rounded-lg border px-4 py-3 opacity-70 transition hover:opacity-100"
                  aria-disabled="true"
                  title="Please contact us to unlock"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <span
                        class="bg-base-300/50 text-base-content/50 flex h-9 w-9 items-center justify-center rounded-lg"
                      >
                        <span class="${prov.icon} text-lg"></span>
                      </span>
                      <div>
                        <div class="text-base-content/70 flex items-center gap-2 font-medium">
                          ${prov.name}
                          <span class="icon-[material-symbols--lock] text-base-content/40 text-sm"></span>
                        </div>
                        <div class="text-base-content/50 text-xs">Coming soon</div>
                      </div>
                    </div>
                    <span class="badge badge-ghost border-base-300 text-base-content/60 gap-1">
                      <span class="icon-[material-symbols--lock] text-xs"></span>
                      Unlock on Request
                    </span>
                  </div>

                  <!-- Hover overlay -->
                  <div
                    class="bg-base-100/85 text-base-content pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-medium opacity-0 backdrop-blur-[1px] transition-opacity duration-200 group-hover:opacity-100"
                  >
                    <span class="flex items-center gap-2">
                      <span class="icon-[material-symbols--mail] text-base"></span>
                      Please contact us to unlock
                    </span>
                  </div>
                </div>
              `,
            )}
          </div>

          <p class="text-base-content/50 mt-3 text-xs">
            After connecting, open a campaign and use “Push to CRM” to send its contacted leads and replies as contacts
            + notes.
          </p>
        </div>
      </div>

      <!-- Deleted Campaigns (recycle bin) -->
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center gap-3">
            <span class="bg-error/10 text-error flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--delete-outline] text-xl"></span>
            </span>
            <div>
              <h2 class="card-title text-lg">Deleted Campaigns</h2>
              <p class="text-base-content/70 text-sm">
                Deleted campaigns stop sending and are hidden from your dashboard, but stay here for
                ${retentionDays} days. Restore one at any point in that window — afterwards it is deleted
                permanently, along with its leads and messages.
              </p>
            </div>
          </div>

          ${
            loadingDeleted
              ? html`<div class="mt-4 flex flex-col gap-2">${skeletonLine()} ${skeletonLine()}</div>`
              : deletedCampaigns.length === 0
                ? html`
                  <div class="text-base-content/60 mt-4 flex items-center gap-2 text-sm">
                    <span class="icon-[material-symbols--check-circle] text-base-content/40 text-lg"></span>
                    No deleted campaigns.
                  </div>
                `
                : html`
                  <div class="mt-4 flex flex-col gap-2">
                    ${deletedCampaigns.map(c => {
                      const daysLeft = c.daysLeft ?? 0;
                      // Under a week left is the point at which "you still have
                      // time" stops being true — flag it rather than letting a
                      // campaign quietly reach its purge date.
                      const urgent = daysLeft <= 7;
                      const busy = deletedBusy === c._id;
                      return html`
                        <div class="border-base-300 flex items-center gap-3 rounded-lg border px-3 py-2">
                          <span class="icon-[material-symbols--campaign] text-base-content/40 text-xl shrink-0"></span>
                          <div class="min-w-0 flex-1">
                            <div class="truncate text-sm font-medium">${c.name || 'Untitled Draft'}</div>
                            <div class="text-base-content/50 text-xs">
                              Deleted ${new Date(c.deletedAt).toLocaleDateString()} ·
                              <span class=${urgent ? 'text-error font-medium' : ''}>
                                ${
                                  daysLeft === 0
                                    ? 'deleted permanently within hours'
                                    : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
                                }
                              </span>
                              ${c.leadsProcessed ? html` · ${c.leadsProcessed} leads` : nothing}
                            </div>
                          </div>
                          <button
                            class="btn btn-outline btn-xs gap-1 ${busy ? 'btn-disabled' : ''}"
                            title="Restore this campaign to your dashboard"
                            @click=${() => void restoreCampaign(c)}
                          >
                            ${
                              busy
                                ? html`<span class="loading loading-spinner loading-xs"></span>`
                                : html`<span class="icon-[material-symbols--restore-from-trash] text-sm"></span>`
                            }
                            Restore
                          </button>
                          <button
                            class="btn btn-ghost btn-xs text-error gap-1 ${busy ? 'btn-disabled' : ''}"
                            title="Delete this campaign permanently — cannot be undone"
                            @click=${() => void purgeCampaign(c)}
                          >
                            <span class="icon-[material-symbols--delete-forever] text-sm"></span>
                            Delete permanently
                          </button>
                        </div>
                      `;
                    })}
                  </div>
                `
          }
        </div>
      </div>
    </div>
  `;
});
